/**
 * The audit log page's filters, its paging state and the query they
 * become (TRA-1626). Pure, so the section test can check what reaches
 * the API.
 */

import type { AuditEvent, AuditEventParams } from '@/hooks/useAuditEvents'

import { rangeStart, type TimeRange } from './auditLabels'

/** The "no filter" choice of a dropdown. */
export const ANY = 'any'
/** The rows-per-page choices; the API takes at most the last one. */
export const PAGE_SIZES = [50, 100, 200] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 50
export const OUTCOMES: AuditEvent['outcome'][] = ['ok', 'denied', 'error']

export interface Filters {
  /** An email picked from the Who list, matched exactly. */
  actor: string
  /** Typed part of an email, matched anywhere in it. */
  actorSearch: string
  action: string
  outcome: string
  range: TimeRange
}

export const DEFAULT_FILTERS: Filters = { actor: '', actorSearch: '', action: ANY, outcome: ANY, range: '7d' }

/** The count the first page answered with. */
export interface PageTotal {
  total: number
  capped: boolean
}

export interface Paging {
  filters: Filters
  pageSize: PageSize
  /** Where each earlier page ended, so Newer goes back one page. */
  cursors: string[]
  /**
   * The first page's count, kept while the reader pages older. The
   * cursor pins the older pages to the rows the first page saw, so the
   * count has to stay pinned too: rows written since (the reader's own
   * "Viewed the audit log" among them) would otherwise add a page that
   * Older can never reach. Null on the first page, which counts live.
   */
  firstTotal: PageTotal | null
}

export const INITIAL_PAGING: Paging = {
  filters: DEFAULT_FILTERS,
  pageSize: DEFAULT_PAGE_SIZE,
  cursors: [],
  firstTotal: null,
}

export type PagingAction =
  | { type: 'filter'; patch: Partial<Filters> }
  | { type: 'pageSize'; pageSize: PageSize }
  | { type: 'older'; cursor: string; total: PageTotal }
  | { type: 'newer' }
  | { type: 'newest' }

/** A filter or page size change starts over from the newest rows. */
export function pagingReducer(state: Paging, action: PagingAction): Paging {
  switch (action.type) {
    case 'filter':
      return { ...state, filters: { ...state.filters, ...action.patch }, cursors: [], firstTotal: null }
    case 'pageSize':
      return { ...state, pageSize: action.pageSize, cursors: [], firstTotal: null }
    case 'older':
      return {
        ...state,
        cursors: [...state.cursors, action.cursor],
        firstTotal: state.firstTotal ?? action.total,
      }
    case 'newer': {
      const cursors = state.cursors.slice(0, -1)
      return { ...state, cursors, firstTotal: cursors.length === 0 ? null : state.firstTotal }
    }
    case 'newest':
      return { ...state, cursors: [], firstTotal: null }
  }
}

/** The page size a dropdown value names, or null for one it does not offer. */
export function toPageSize(value: string): PageSize | null {
  return PAGE_SIZES.find((size) => String(size) === value) ?? null
}

/** The query the API takes for the filters and the page. */
export function auditParams(
  filters: Filters,
  cursor: string | null,
  now: Date,
  pageSize: PageSize,
): AuditEventParams {
  return {
    limit: pageSize,
    cursor: cursor ?? undefined,
    actor: filters.actor.trim() === '' ? undefined : filters.actor.trim(),
    actor_search: filters.actorSearch.trim() === '' ? undefined : filters.actorSearch.trim(),
    action: filters.action === ANY ? undefined : filters.action,
    outcome: filters.outcome === ANY ? undefined : (filters.outcome as AuditEvent['outcome']),
    from: rangeStart(filters.range, now),
  }
}

export interface PagePosition {
  /** "Rows 51 to 100 of 312", or "of 100,000+" past the API's count cap. */
  rows: string
  /** "Page 2 of 7". */
  pages: string
}

const count = (value: number): string => value.toLocaleString('en-US')

/**
 * Where the reader is: `page` is the zero-based page index, `shown` the
 * rows on it, `total` the rows the filters match (at most the API's cap
 * when `capped`).
 */
export function pagePosition(
  page: number,
  pageSize: PageSize,
  shown: number,
  total: number,
  capped: boolean,
): PagePosition {
  const first = page * pageSize + 1
  const last = page * pageSize + shown
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const more = capped ? '+' : ''
  return {
    rows: `Rows ${count(first)} to ${count(last)} of ${count(total)}${more}`,
    pages: `Page ${count(page + 1)} of ${count(pages)}${more}`,
  }
}

/** What the Who filter holds: nobody, one picked email, or typed text. */
export interface ActorChoice {
  /** An email picked from the list, matched exactly. */
  actor: string
  /** Typed part of an email, matched anywhere in it. */
  actorSearch: string
}

/** The words on the closed filter for the current choice. */
export function actorChoiceLabel(choice: ActorChoice): string {
  if (choice.actor !== '') return choice.actor
  if (choice.actorSearch !== '') return `Matches for “${choice.actorSearch}”`
  return 'Anyone'
}

/** The people whose email holds `typed`, in any case; all of them for none. */
export function matchingPeople(people: readonly string[], typed: string): readonly string[] {
  const needle = typed.toLowerCase()
  return needle === '' ? people : people.filter((person) => person.toLowerCase().includes(needle))
}
